import { useState } from 'react'

import { API } from '@/api'
import { Button, Input, message, Modal } from '@/components'
import { useUserStore } from '@/stores/user'

interface InitPasswordModalProps {
  onSuccess: () => void
}

export function InitPasswordModal({ onSuccess }: InitPasswordModalProps) {
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [oldPasswordError, setOldPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const userStore = useUserStore()

  const handleOldPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOldPassword(e.target.value)
    setOldPasswordError('')
  }

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setConfirmPassword(value)
    if (value && value !== newPassword) {
      setConfirmPasswordError('The two passwords do not match')
    } else {
      setConfirmPasswordError('')
    }
  }

  const handleSubmit = async () => {
    // Validate all fields
    let hasError = false

    if (!oldPassword) {
      setOldPasswordError('Please enter your old password')
      hasError = true
    }

    if (!newPassword) {
      hasError = true
    }

    if (!confirmPassword) {
      setConfirmPasswordError('Please re-enter your new password')
      hasError = true
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordError('The two passwords do not match')
      hasError = true
    }

    if (hasError) {
      return
    }

    setSubmitting(true)
    try {
      const { code, message: serverMessage } = await API.updatePassword({
        name: userStore.name,
        ori_password: oldPassword,
        password: newPassword,
      })

      if (code === 0) {
        message.success('Password changed successfully')
        userStore.clearNeedInitPassword()
        onSuccess()
      } else {
        // The server is the sole validator of the old password.
        setOldPasswordError(serverMessage || 'The old password is incorrect')
      }
    } catch (error) {
      console.error('Failed to change password:', error)
      message.error('Failed to change password')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={true} className="w-[480px]">
      <div className="mb-4">
        <h2 className="h6 text-primary">Change Password</h2>
      </div>

      <div className="flex flex-col gap-4">
        {/* Old Password */}
        <div>
          <label className="t-sb text-primary block mb-2">
            Enter your <span className="text-highlight">old</span> password
          </label>
          <Input
            type="password"
            placeholder="****************"
            className="t-sm"
            value={oldPassword}
            onChange={handleOldPasswordChange}
            disabled={submitting}
          />
          {oldPasswordError && <p className="t-sm text-error mt-1">{oldPasswordError}</p>}
        </div>

        {/* New Password */}
        <div>
          <label className="t-sb text-primary block mb-2">
            Enter your <span className="text-highlight">new</span> password
          </label>
          <Input
            type="password"
            placeholder="****************"
            className="t-sm"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitting}
          />
        </div>

        {/* Confirm New Password */}
        <div>
          <label className="t-sb text-primary block mb-2">
            Re-enter your <span className="text-highlight">new</span> password
          </label>
          <Input
            type="password"
            placeholder="****************"
            className="t-sm"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            disabled={submitting}
          />
          {confirmPasswordError && <p className="t-sm text-error mt-1">{confirmPasswordError}</p>}
        </div>

        {/* Submit Button */}
        <div className="pt-2">
          <Button
            type="secondary"
            block
            className="t-sb"
            onClick={handleSubmit}
            disabled={submitting}
          >
            Submit
          </Button>
        </div>
      </div>
    </Modal>
  )
}
